import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container, Icon, Header, Label, Sidebar, Button, Grid, Message, Table, Input, Image, List } from 'semantic-ui-react';
import i18next from 'i18next';

import { DataGrid } from '../../common';
import SideEditor from '../../general/SideEditor';
import { formatPrice } from '../../../helpers/format';
import {
    fetchLoyaltyProgramBenefits,
    fetchLoyaltyProgramRestaurants,
    fetchLoyaltyProgramCampaigns,
    deleteBenefit,
    setEditedEntity,
    confirmDelete
} from '../../../actions';
import { getGlobalBenefits, getPersonalBenefits } from '../../../selectors';

import RestaurantThumbnail from '../../../assets/icons/restaurant-icon.svg';
import BenefitsEditor from './BenefitsEditor';
import './Benefits.scss';

class Benefits extends Component {

    state = {
        activeIndex: 0
    };

    constructor(props) {
        super(props);

        this.renderRowExpansion = this.renderRowExpansion.bind(this);
        this.renderCreationButton = this.renderCreationButton.bind(this);
        this.renderBenefitType = this.renderBenefitType.bind(this);
        this.renderFunding = this.renderFunding.bind(this);
        this.renderValidAt = this.renderValidAt.bind(this);
        this.figureOutBenebfitState = this.figureOutBenebfitState.bind(this);
        this.renderCampaignsAssociation = this.renderCampaignsAssociation.bind(this);
        this.handleRemoval = this.handleRemoval.bind(this);
    }

    componentWillMount() {
        const {
            loyaltyProgram,
            fetchLoyaltyProgramBenefits,
            fetchLoyaltyProgramCampaigns,
            fetchLoyaltyProgramRestaurants
        } = this.props;

        if (loyaltyProgram && loyaltyProgram.id) {
            fetchLoyaltyProgramBenefits(loyaltyProgram.id);
            fetchLoyaltyProgramCampaigns(loyaltyProgram.id);

            if (loyaltyProgram && loyaltyProgram.restaurantsIds) {
                const lpRestaurants = loyaltyProgram.name === 'Paidit' ? null : loyaltyProgram.restaurantsIds;
                fetchLoyaltyProgramRestaurants(loyaltyProgram.id, lpRestaurants);
            }
        }
    }

    handleRemoval(benefit) {
        const { confirmDelete, deleteBenefit, loyaltyProgram } = this.props;
        confirmDelete(benefit.description, () => deleteBenefit(loyaltyProgram.id, benefit.id));
    }

    figureOutBenebfitState(issues) {
        let benefitState = "inactive";

        for (let issue of issues) {
            const now = new Date();

            if (!issue.closeDate && issue.expirationDate && now > new Date(issue.expirationDate)) {
                benefitState = "expired";
            } else if (!issue.closeDate && (!issue.expirationDate || now < new Date(issue.expirationDate))) {
                if (benefitState === "inactive") benefitState = "active";
            }
        }

        return benefitState;
    }

    renderValidAt(validAt) {
        const { restaurants } = this.props;

        if (!validAt || !validAt.restaurants || (Array.isArray(validAt.restaurants) && !validAt.restaurants.length)) {
            return i18next.t("allRestaurants");
        }

        if (validAt.restaurants.length === 1) {
            const validRestaurant = restaurants.find(rest => rest.id === validAt.restaurants[0]);
            if (validRestaurant) return validRestaurant.name;
        }

        return i18next.t(`loyaltyProgram.validAtMultiple`);
    }

    renderFunding(funder) {
        if (funder === 'RESTAURANTS') return 'restaurants';
        if (funder === 'LOYALTY_PROGRAM') return 'loyaltyProgram';
        return 'Paidit';
    }

    renderBenefitType({ financialType, value, categoryId, itemId }) {
        const { restaurants } = this.props;
        const currencyCode = restaurants[0] ? restaurants[0].currencyCode : "ILS";

        switch (financialType) {
            case 'PERCENT':
                return value + '% ' + i18next.t('discountOnEntireMenu').toLowerCase();
            case 'AMOUNT':
                return formatPrice(value, currencyCode) + ' ' + i18next.t('gift').toLowerCase();
            default:
                return value;
        }
    }

    renderCampaignsAssociation(issues) {
        return issues.filter(issue => issue.campaignId).length;
    }

    renderCreationButton() {
        const { setEditedEntity } = this.props;

        return (
            <Button
                icon
                labelPosition='left'
                primary
                onClick={() => setEditedEntity('benefit')}
                floated='right'
            >
                <Icon name='add' /> {i18next.t('create')}
            </Button>
        );
    }

    renderRowExpansion(row) {
        const { restaurants } = this.props;

        const benefit = row.original;
        let validAt;

        if (benefit.validAt && benefit.validAt.restaurants.length) {
            validAt = (
                <List horizontal>
                    {restaurants.filter(rest => {
                        return benefit.validAt.restaurants.some(restId => restId === rest.id);
                    }).map(restaurant => (
                        <List.Item key={restaurant.id}>
                            <Image avatar src={restaurant.thumbnail ? restaurant.thumbnail : RestaurantThumbnail} />
                            <List.Content>
                                <List.Header as='a'>{restaurant.name}</List.Header>
                            </List.Content>
                        </List.Item>
                    ))}
                </List>
            );
        } else if (benefit.validAt) {
            validAt = i18next.t('allRestaurants');
        } else {
            validAt = i18next.t('error');
        }

        return (
            <Container className='pi-row-expansion pi-benefit-expansion'>
                <Header size='medium'>
                    <Header.Content>{i18next.t('loyaltyProgram.validAt')}: </Header.Content>
                </Header>
                <div className='pi-benefit-expansion-valid-at'>
                    {validAt}
                </div>

            </Container>
        );
    }

    render() {
        const { loyaltyProgram, benefits, history, onEdit, setEditedEntity } = this.props;

        if (!loyaltyProgram) return null;

        const columns = [{
            Header: '#',
            maxWidth: 50,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' },
            accessor: 'id'
        }, {
            Header: i18next.t('loyaltyProgram.benefitName'),
            accessor: 'description',
            filterable: true
        }, {
            id: 'type',
            Header: i18next.t('loyaltyProgram.benefitType'),
            accessor: benefit => this.renderBenefitType(benefit.type),
            filterable: true
        }, {
            id: 'validAt',
            Header: i18next.t('loyaltyProgram.validAt'),
            accessor: benefit => this.renderValidAt(benefit.validAt),
            filterable: true
        }, {
            id: 'funder',
            Header: i18next.t('funding'),
            accessor: benefit => this.renderFunding(benefit.funder),
            Cell: row => {
                if(row.value === 'loyaltyProgram') return i18next.t('loyaltyProgram.self');
                return i18next.t(row.value);
            },
            filterable: true,
            filterValues: [
                { value: 'loyaltyProgram', text: i18next.t('loyaltyProgram.self') },
                { value: 'restaurants', text: i18next.t('restaurants') }
            ]
        }, {
            id: 'status',
            Header: i18next.t('activityStatus'),
            accessor: benefit => this.figureOutBenebfitState(benefit.issues),
            Cell: row => i18next.t(row.value),
            filterable: true,
            filterValues: [
                { value: 'active', text: i18next.t('active') },
                { value: 'inactive', text: i18next.t('inactive') },
                { value: 'expired', text: i18next.t('expired') }
            ]
        }, {
            id: 'campaigns',
            Header: i18next.t('loyaltyProgram.campaigns'),
            accessor: benefit => this.renderCampaignsAssociation(benefit.issues),
            filterable: true,
            filterMinMax: { min: 0, max: 1000 }
        }, {
            id: 'actions',
            Header: i18next.t('actions'),
            maxWidth: 120,
            sortable: false,
            filterable: false,
            Cell: row => {
                const benefit = row.original;

                return (
                    <div>
                        <Button
                            circular
                            size='tiny'
                            icon='find'
                            primary
                            onClick={() => history.push(`/loyaltyProgram/${loyaltyProgram.id}/benefits/${benefit.id}`)} />
                        <Button circular size='tiny' icon='pencil' onClick={() => setEditedEntity('benefit', benefit, { hasIssues: !!benefit.issues.length })} />
                        {
                            !benefit.issues.length ?
                                <Button key="btn2" circular size='tiny' icon='trash' negative onClick={() => this.handleRemoval(benefit)} />
                                : null
                        }
                    </div>
                );
            }
        }];

        return (
            <div style={{ padding: 10 }}>
                <SideEditor
                    title={i18next.t('loyaltyProgram.benefits')}
                    visible={onEdit}
                    handleClose={() => setEditedEntity(null)}
                >
                    <BenefitsEditor loyaltyProgramId={loyaltyProgram.id} />
                </SideEditor>
                <DataGrid
                    data={benefits}
                    columns={columns}
                    getTrGroupProps={(state, rowInfo, column) => {
                        if (typeof rowInfo !== "undefined" && rowInfo.original && rowInfo.original.issues && rowInfo.original.issues.length) {
                            const benefitStatus = this.figureOutBenebfitState(rowInfo.original.issues);

                            if (benefitStatus === "active") {
                                return { className: 'pi-data-grid-row-positive' };
                            } else if (benefitStatus === "expired") {
                                return { className: 'pi-data-grid-row-negative' };
                            }
                        }
                        return {};
                    }}
                    SubComponent={this.renderRowExpansion}
                    extraActions={this.renderCreationButton()}
                    paginated
                   
                />
            </div>
        );
    }

}

Benefits.propTypes = {
    fetchLoyaltyProgramBenefits: PropTypes.func,
    fetchLoyaltyProgramCampaigns: PropTypes.func,
    fetchLoyaltyProgramRestaurants: PropTypes.func,
    deleteBenefit: PropTypes.func,
    loyaltyProgram: PropTypes.object,
    benefits: PropTypes.array,
    campaigns: PropTypes.array,
    restaurants: PropTypes.array,
    history: PropTypes.object,
    onEdit: PropTypes.bool,
    setEditedEntity: PropTypes.func,
    confirmDelete: PropTypes.func,
    
};

const actions = {
    fetchLoyaltyProgramBenefits,
    fetchLoyaltyProgramRestaurants,
    fetchLoyaltyProgramCampaigns,
    deleteBenefit,
    setEditedEntity,
    confirmDelete
};

function mapStateToPropsFactory(benefitsSelectorFunction) {
    return (state) => {
        const { campaigns, restaurants } = state.loyaltyApp;
        const benefits = benefitsSelectorFunction(state);
        const onEdit = state.editor.type === 'benefit';

        return { benefits, campaigns, restaurants, onEdit };
    };
}

export const GlobalBenefits = connect(mapStateToPropsFactory(getGlobalBenefits), actions)(Benefits);
export const PersonalBenefits = connect(mapStateToPropsFactory(getPersonalBenefits), actions)(Benefits);